import React, { useEffect, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import { orderBy } from 'lodash-es';
import Spinner from '@strava/ui/Spinner';
import useFacebook from 'hooks/externalSocialPartners/useFacebook';
import { AuthorizationStatus } from 'hooks/externalSocialPartners/constants/auth-status-constants';
import StravaAlert from 'components/StravaAlert';
import { INVITE_FRIENDS, ONBOARDING } from '../utils/friendsConstants';
import { t } from '../utils/friendsUtils';
import FriendsContext from '../state/FriendsContext';
import { reducer } from '../state/friendsReducer';
import { ADD_ALERT, UPDATE_FRIENDS } from '../state/friendsActions';
import LinkFacebookAccount from '../components/LinkFacebookAccount';
import FacebookFriendList from '../components/FacebookFriendList';

import styles from './FindFacebookFriends.scss';

const FindFacebookFriends = ({
  track,
  currentAthlete,
  isVisible, // InviteFriends modal -> modalTablIndex === 1
  source, // 'invite_friends' or 'onboarding'
  fbApiProps,
  onFollowingFriendsChange
}) => {
  const [state, dispatch] = useReducer(reducer, {
    currentAthlete,
    source,
    friends: {},
    followedFriends: {},
    alert: null,
    onFollowingFriendsChange
  });
  const { alert } = state;

  const providerState = useMemo(() => ({ state, dispatch, track }), [
    state,
    track
  ]);

  const { fbFriends, authorizationStatus, requestFriends } = useFacebook({
    ...fbApiProps,
    source
  });

  useEffect(() => {
    // When component is visible, call requestFriends as soon as FB script is ready.
    // If user is not already logged in to FB, or if user is logged in to FB but
    // doesn't have the right permissions, auth status will be REQUEST_NEW_PERMISSIONS
    // and we show them the LinkFacebookAccount view
    if (isVisible && authorizationStatus === AuthorizationStatus.READY) {
      requestFriends();
    }
    // requestFriends deliberately left out to prevent multiple calls to this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationStatus, isVisible]);

  // save friends to the shared state
  useEffect(() => {
    if (fbFriends.length > 0) {
      // INVITE_FRIENDS: list followable friends before friends already being followed
      // and sort alphabetically by name
      // ONBOARDING: list only followable friends
      const processedFriends =
        source === INVITE_FRIENDS
          ? orderBy(fbFriends, ['isFollowable', 'displayName'], ['desc', 'asc'])
          : fbFriends.filter((friend) => friend.isFollowable);
      // map friends array to object with the strava ids as the keys
      // save the index of the friend's position in the sorted array
      const friends = Object.fromEntries(
        processedFriends.map((friend, index) => [
          friend.id,
          { ...friend, sortedIndex: index }
        ])
      );
      dispatch({
        type: UPDATE_FRIENDS,
        payload: { friends }
      });
    }
  }, [dispatch, fbFriends, source]);

  useEffect(() => {
    if (authorizationStatus === AuthorizationStatus.ERROR) {
      // show error message to athlete when something goes wrong during authorization
      dispatch({
        type: ADD_ALERT,
        payload: {
          alert: { severity: 'error', message: t('errors.fb_error') }
        }
      });
    } else if (
      source === INVITE_FRIENDS &&
      authorizationStatus === AuthorizationStatus.UNAUTHORIZED
    ) {
      // INVITE_FRIENDS ONLY:
      // show warning message to athlete if they don't authorize FB permissions
      dispatch({
        type: ADD_ALERT,
        payload: {
          alert: {
            severity: 'warning',
            message: t('errors.unauthorized')
          }
        }
      });
    }
  }, [authorizationStatus, dispatch, source]);

  return (
    <FriendsContext.Provider value={providerState}>
      <div className={styles.fbPanel}>
        {source === INVITE_FRIENDS && alert && (
          <StravaAlert severity={alert.severity}>{alert.message}</StravaAlert>
        )}
        {source === ONBOARDING && <p>{t('onboarding.message')}</p>}
        {[
          AuthorizationStatus.REQUEST_NEW_PERMISSIONS,
          AuthorizationStatus.ERROR,
          AuthorizationStatus.UNAUTHORIZED
        ].includes(authorizationStatus) && (
          <LinkFacebookAccount
            requestFriends={requestFriends}
            authorizationStatus={authorizationStatus}
          />
        )}
        {source === ONBOARDING && alert && (
          <StravaAlert
            severity={alert.severity}
            className={styles.noBackground}
          >
            {alert.message}
          </StravaAlert>
        )}
        {authorizationStatus === AuthorizationStatus.DATA_FETCHED && (
          <FacebookFriendList />
        )}
        {[
          AuthorizationStatus.INITIATED,
          AuthorizationStatus.READY,
          AuthorizationStatus.AUTHORIZED
        ].includes(authorizationStatus) && (
          <div className={styles.centerSpinner}>
            <Spinner size={48} />
          </div>
        )}
      </div>
    </FriendsContext.Provider>
  );
};

FindFacebookFriends.defaultProps = {
  isVisible: true,
  onFollowingFriendsChange: () => {}
};

FindFacebookFriends.propTypes = {
  track: PropTypes.func.isRequired,
  currentAthlete: PropTypes.shape({
    id: PropTypes.number
  }).isRequired,
  fbApiProps: PropTypes.shape({
    appId: PropTypes.number,
    version: PropTypes.string,
    locale: PropTypes.string
  }).isRequired,
  source: PropTypes.oneOf([INVITE_FRIENDS, ONBOARDING]).isRequired,
  isVisible: PropTypes.bool,
  onFollowingFriendsChange: PropTypes.func
};

export default FindFacebookFriends;
